import {
  EditSettingsModel,
  SortSettingsModel
} from "@syncfusion/ej2-react-grids";
import React, { ReactElement, useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import { useAuthDataContext } from "../../components/authDataProvider";
import { BreadCrumbsNavigation } from "../../components/breadCrumbsNavigation";
import { DataGrid } from "../../components/dataGrid";
import {
  fetchContractsByProjectCostId,
  setPropertyToProjectCostContract
} from "../../helpers/fetching/CostContractsServiceHelper";
import { isAnyCommonString } from "../../helpers/filtering/FilteringHelper";
import { updateTotalCostContractInProjectAndProjectActivityAndProjectCost } from "../../helpers/updateParent/UpdateParentHelper";
import { DataGridColumnModel } from "../../models/DataGridColumnModel";
import { ProjectCostContractBaseModel } from "../../models/ProjectCostContractBaseModel";
import { GetFirstObjectById } from "../../services/GenericServices";
import { roleListAdminAndEmployee } from "../../settings/Constants";
import { Language, ParseClassName } from "../../settings/Enums";
import { translations } from "../../settings/translation";

interface Props {}

export const ProjectCostContracts: React.FC<Props> = (): ReactElement => {
  const [projectCostContracts, setProjectCostContracts] = useState<
    ProjectCostContractBaseModel[]
  >([]);
  const [projectCost, setProjectCost] = useState<any | undefined>(undefined);
  const history = useHistory();
  const { userRoles, selectedOrganization, language } = useAuthDataContext();

  useEffect(() => {
    getProjectActivityIdFromRoute();
  }, []);

  let projectId: string;
  let activityId: string;
  let costId: string;

  const getProjectActivityIdFromRoute = async (): Promise<void> => {
    const url = window.location.pathname;
    const urlParts = url.split("/");
    if (
      urlParts &&
      urlParts.length === 8 &&
      urlParts[1] === "projects" &&
      urlParts[3] === "activities" &&
      urlParts[5] === "costs" &&
      urlParts[7] === "contracts"
    ) {
      projectId = urlParts[2];
      activityId = urlParts[4];
      costId = urlParts[6];

      const projectCostResult = await GetFirstObjectById(
        "ProjectCost",
        costId,
        "projectActivity.project.role"
      );
      const projectCostContracts = await fetchContractsByProjectCostId(
        projectCostResult
      );
      setProjectCost(projectCostResult);
      setProjectCostContracts(projectCostContracts);
    }
  };

  const editSettings: EditSettingsModel = {
    allowEditing:
      userRoles &&
      isAnyCommonString(
        userRoles,
        roleListAdminAndEmployee,
        selectedOrganization?.attributes?.shortTitle
      )
        ? true
        : false,
    allowAdding:
      userRoles &&
      isAnyCommonString(
        userRoles,
        roleListAdminAndEmployee,
        selectedOrganization?.attributes?.shortTitle
      )
        ? true
        : false,
    mode: "Dialog",
  };

  const sortSettings: SortSettingsModel = {
    columns: [{ field: "contractor", direction: "Ascending" }],
  };

  const columns: DataGridColumnModel[] = [
    {
      field: "id",
      visible: false,
      isPrimaryKey: true,
      allowEditing: false,
    },
    {
      field: "contractor",
      headerText: translations[language || Language.Hr].suppllier,
      validationRules: {
        required: true,
      },
    },
    {
      field: "tagTitle",
      headerText: translations[language || Language.Hr].tag,
    },
    {
      field: "unitPrice",
      headerText: translations[language || Language.Hr].unitPriceShort,
      validationRules: {
        required: true,
      },
      format: "N2",
    },
    {
      field: "numberOfUnits",
      headerText: translations[language || Language.Hr].quantity,
      validationRules: {
        required: true,
      },
    },
    {
      field: "realTotalNetPrice",
      headerText: translations[language || Language.Hr].overallAcceptableShort,
      validationRules: {
        required: true,
      },
      format: "N2",
    },
    {
      field: "vatRate",
      headerText: translations[language || Language.Hr].vatRate,
      validationRules: {
        required: true,
      },
      format: "P2",
    },
    {
      field: "vatAmount",
      headerText: translations[language || Language.Hr].vatAmount,
      validationRules: {
        required: true,
      },
      format: "N2",
    },
    {
      field: "realTotalVatPrice",
      headerText: translations[language || Language.Hr].inTotalWithVAT,
      validationRules: {
        required: true,
      },
      format: "N2",
    },
    {
      field: "subventionAmount",
      headerText: translations[language || Language.Hr].subvention,
      format: "N2",
    },
    {
      field: "totalPayment",
      headerText: translations[language || Language.Hr].paid,
      format: "N2",
    },
    {
      field: "details",
      headerText: translations[language || Language.Hr].details,
      allowEditing: false,
      allowSorting: false,
      allowFiltering: false,
      width: 100,
      template: (props): ReactElement => {
        return (
          <div>
            {projectId && activityId && (
              <div
                className="e-btn"
                onClick={(): void =>
                  history.push(
                    `/projects/${projectId}/activities/${activityId}/costs/${costId}/contracts/${props.id}/payments`
                  )
                }
              >
                {translations[language || Language.Hr].payments}
              </div>
            )}
          </div>
        );
      },
    },
  ];

  const createOrUpdateProjectCostContract = async (
    newProjectCostContract: ProjectCostContractBaseModel
  ): Promise<void> => {
    const projectCostContract = await setPropertyToProjectCostContract(
      newProjectCostContract,
      projectCost,
      selectedOrganization
    );
    const response = await projectCostContract.save();

    if (response) {
      const projectCostContracts = await fetchContractsByProjectCostId(
        projectCost
      );
      setProjectCostContracts(projectCostContracts);

      await updateTotalCostContractInProjectAndProjectActivityAndProjectCost(
        projectCost?.id
      );
    }
  };

  return (
    <section id="cost">
      <BreadCrumbsNavigation
        breadCrumbTemplates={[
          {
            url: "projects",
            title: translations[language || Language.Hr].projects,
          },
          {
            url: "activities",
            title: translations[language || Language.Hr].activities,
          },
          {
            url: "costs",
            title: translations[language || Language.Hr].costs,
          },
        ]}
      />
      <Row className={"box-form"}>
        <Col>
          <DataGrid
            data={projectCostContracts}
            columnProperties={columns}
            useDialogTemplate={ParseClassName.ProjectCostContract}
            createItem={createOrUpdateProjectCostContract}
            updateItem={createOrUpdateProjectCostContract}
            sortSettings={sortSettings}
            editSettings={editSettings}
            allowPaging={true}
            autoFitColumns={true}
          />
        </Col>
      </Row>
    </section>
  );
};
